import Info from "@assets/icons/info.svg";
import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";
import { transactionApiServices } from "@services/apis/Transactions";
import { GetCPGTransactionListData } from "@services/models/Transactions/GetCPGTransactionListModel";
import {
  SettleTransactionRequest,
  SettleTransactionResponse,
} from "@services/models/Transactions/SettleTransactionModel";
import React, { useEffect, useState } from "react";
import ConstantFunctions from "../../../utils/constants_functions";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import PrimeDetailsActionButton from "../../Prime/components/PrimeDetailsActionButton";
import CPGOrderFlowDetailsSkeleton from "./components/CPGOrderFlowDetailsSkeleton";
import MissingTransactionSlider from "./components/MissingTransaction";
import RedirectButton from "./components/RedirectButton";
import { downloadCpgPaymentReceiptPdf } from "./receipts/downloadCpgPaymentReceiptPdf";
import {
  extractCpgPayeeDetails,
  mapCpgTransactionToReceipt,
} from "./receipts/mapCpgTransactionToReceipt";

interface CPGOrderFlowDetailsProps {
  transactionId: number;
}
const CPGOrderFlowDetails: React.FC<CPGOrderFlowDetailsProps> = ({
  transactionId,
}) => {
  const [data, setData] = useState<GetCPGTransactionListData | undefined>(
    undefined,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (transactionId) {
      setIsLoading(true);
      transactionApiServices
        .getCPGTransactionDetails({ transactionId })
        .then((response) => {
          setData(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          handleError({ message: error.message });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [transactionId]);

  const constantFunctions = new ConstantFunctions();

  const [openMissingTransactionSlider, setOpenMissingTransactionSlider] =
    useState<boolean>(false);
  const [isSettleTransactionButtonLoading, setIsTransactionButtonLoading] =
    useState<boolean>(false);
  const [isSendCallBackButtonLoading, setIsSendCallBackButtonLoading] =
    useState<boolean>(false);
  const [
    isCancelTransactionButtonLoading,
    setIsCancelTransactionButtonLoading,
  ] = useState<boolean>(false);
  const [isReceiptLoading, setIsReceiptLoading] = useState<boolean>(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSettleTransaction = async () => {
    try {
      setIsTransactionButtonLoading(true);
      const settleTransactionRequest: SettleTransactionRequest = {
        transactionId: data?.trxnId,
      };

      const response: SettleTransactionResponse =
        await transactionApiServices.settleTransaction(
          settleTransactionRequest,
        );
      handleSuccess({
        message: response.msg || "Transaction settled successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating transction failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsTransactionButtonLoading(false);
    }
  };

  const handleCancelTransaction = async () => {
    try {
      setIsCancelTransactionButtonLoading(true);
      const settleTransactionRequest: SettleTransactionRequest = {
        transactionId: data?.trxnId,
        merchantOrderId: data?.orderId,
      };

      const response: SettleTransactionResponse =
        await transactionApiServices.settleTransaction(
          settleTransactionRequest,
          "/adminPanel/cancelMerchantTransaction",
        );
      handleSuccess({
        message: response.msg || "Transaction cancelled successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating transction failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsCancelTransactionButtonLoading(false);
    }
  };

  const canGenerateReceipt =
    data?.status?.toLowerCase() === "completed" ||
    data?.status?.toLowerCase() === "over payment" ||
    data?.status?.toLowerCase() === "under payment";

  const handleGenerateReceipt = async () => {
    if (!data || !canGenerateReceipt) return;

    try {
      setIsReceiptLoading(true);
      let payee: ReturnType<typeof extractCpgPayeeDetails> = null;
      const merchantOrderId = data.merchantOrderId?.trim();
      if (merchantOrderId) {
        try {
          const payload =
            await transactionApiServices.getTransactionPayloadCounterparty(
              merchantOrderId,
            );
          payee = extractCpgPayeeDetails(payload.data?.transactionPayload);
        } catch {
          payee = null;
        }
      }
      await downloadCpgPaymentReceiptPdf(
        mapCpgTransactionToReceipt(data, payee),
      );
      handleSuccess({ message: "Receipt PDF downloaded." });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to generate receipt. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsReceiptLoading(false);
    }
  };

  const handleSendCallBack = async () => {
    try {
      setIsSendCallBackButtonLoading(true);
      const settleTransactionRequest: SettleTransactionRequest = {
        transactionId: data?.trxnId,
        merchantOrderId: data?.orderId,
      };

      const response: SettleTransactionResponse =
        await transactionApiServices.settleTransaction(
          settleTransactionRequest,
          "/adminPanel/sendCPGWebhook",
        );
      handleSuccess({
        message: response.msg || "Transaction callback sent successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Sending callback failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsSendCallBackButtonLoading(false);
    }
  };

  if (isLoading) {
    return <CPGOrderFlowDetailsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-[12px] mt-[16px]">
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
            {data?.ledger == "CR" ? "Received" : "Sent"}
          </div>
          <DataPill value={data?.status} />
        </div>
        <LabelRow label="Transaction Type" value={data?.transactionType} />
        <LabelRow label="Source" value={data?.source ?? "- -"} />
        <LabelRow
          label="Trx Date & Time"
          value={
            data?.tdate ? constantFunctions.formatDateTime(data.tdate) : "- -"
          }
        />
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label="MID | UID"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {data?.merchantId}
              </span>
              <CopyButton
                copyText={data?.merchantId?.toString() ?? ""}
                copyMessage="Copied to clipboard"
              />
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {data?.userId}
              </span>
              <CopyButton
                copyText={data?.userId?.toString() ?? ""}
                copyMessage="Copied to clipboard"
              />
            </div>
          }
        />
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row items-center gap-[4px]">
          <div className="bg-bg-page-body rounded-full h-[24px] p-[2px] w-[24px] mr-[4px] flex items-center justify-center">
            <img src={Info} alt="Info" />
          </div>
          <span className="text-[14px] font-normal text-text-base">
            Payment Details
          </span>
        </div>
        <LabelRow
          label="Base Currency"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img src={data?.fci} height={14} width={14} alt="currency icon" />
              <p className="text-text-base text-[12px] font-normal">
                {data?.fcs}
              </p>
            </div>
          }
        />
        <LabelRow
          label="Settled Currency"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img src={data?.tci} height={14} width={14} alt="currency icon" />
              <p className="text-text-base text-[12px] font-normal">
                {data?.tcs}
              </p>
            </div>
          }
        />
        <LabelRow label="Network" value={data?.networkSymbol} />
        <LabelRow
          label="Order ID"
          value={data?.orderId ? data.orderId : "- -"}
          isCopy={!!data?.orderId}
        />
        <LabelRow
          label="MOID"
          value={data?.merchantOrderId ?? ""}
          valueComp={
            <TruncatedCopyValue
              value={data?.merchantOrderId}
              copyMessage="MOID copied to clipboard"
            />
          }
        />
        <LabelRow
          label="To address"
          value={data?.systemAddress ? data.systemAddress : "- -"}
          isCopy={!!data?.systemAddress}
          alignment="col"
        />
        <LabelRow
          label="System Notes"
          valueComp={
            <span className="text-text-base text-[12px] font-normal">
              {data?.systemNotes !== "" ? data?.systemNotes : "- -"}
            </span>
          }
          alignment="col"
        />
        <LabelRow
          label="User Notes"
          valueComp={
            <span className="text-text-base text-[12px] font-normal">
              {data?.userNotes !== "" ? data?.userNotes : "- -"}
            </span>
          }
          alignment="col"
        />
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label="Requested in Base"
          value={`${constantFunctions.formatAmount(data?.baseAmount ?? 0)} ${
            data?.fcs
          }`}
        />
        <LabelRow
          label="Requested in Settled"
          value={`${constantFunctions.formatAmount(
            data?.settledAmountRequested ?? 0,
          )} ${data?.tcs}`}
        />
        <LabelRow
          label={data?.ledger === "CR" ? "Received in base" : "Sent in base"}
          value={`${constantFunctions.formatAmount(data?.baseAmountReceived ?? 0)} ${
            data?.fcs
          }`}
        />
        <LabelRow
          label={data?.ledger === "CR" ? "Received" : "Sent"}
          value={`${
            data?.ledger === "CR"
              ? constantFunctions.formatAmount(data?.settledAmountReceived ?? 0)
              : constantFunctions.formatAmount(data?.settledAmountSent ?? 0)
          } ${data?.tcs}`}
        />
        <LabelRow
          label="Commissions"
          value={
            data?.settledAmountReceived === 0 && data.isFinal === 1
              ? `0 ${data?.tcs}`
              : `${Number(data?.commissionAmount)} ${data?.tcs}`
          }
        />
        <LabelRow
          label={data?.ledger === "CR" ? "Credited" : "Debited"}
          value={`${
            data?.ledger === "CR"
              ? constantFunctions.formatAmount(data?.settledAmountCredited ?? 0)
              : constantFunctions.formatAmount(data?.settledAmountDebited ?? 0)
          } ${data?.tcs}`}
        />

        <div className="flex flex-row flex-wrap items-center gap-2">
          <PrimeDetailsActionButton
            label="Settle"
            icon="check"
            onClick={handleSettleTransaction}
            loading={isSettleTransactionButtonLoading}
            disabled={
              !(
                (data?.settledAmountReceived ?? 0) > 0 &&
                (data?.settledAmountReceived ?? 0) <
                  (data?.settledAmountRequested ?? 0) &&
                data?.isFinal === 0
              )
            }
          />
          <PrimeDetailsActionButton
            label="Send callback"
            icon="send"
            onClick={handleSendCallBack}
            loading={isSendCallBackButtonLoading}
          />
          <PrimeDetailsActionButton
            label="Cancel"
            icon="close"
            tone={
              data?.isFinal === 0 && data?.transactionType === "Merchant Payout"
                ? "danger"
                : "default"
            }
            onClick={handleCancelTransaction}
            loading={isCancelTransactionButtonLoading}
            disabled={
              !(
                data?.isFinal === 0 &&
                data?.transactionType === "Merchant Payout"
              )
            }
          />
          {data?.transactionType === "Merchant Payin" &&
            data?.status.toLowerCase() !== "expired" && (
              <PrimeDetailsActionButton
                label="Missing trx"
                icon="search"
                onClick={() => setOpenMissingTransactionSlider(true)}
                skipBusy
              />
            )}
          {canGenerateReceipt && (
            <PrimeDetailsActionButton
              label="Receipt"
              icon="receipt_long"
              onClick={handleGenerateReceipt}
              loading={isReceiptLoading}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="rounded-lg flex flex-col my-2">
          <div className="flex flex-col rounded-lg p-[8px] bg-bg-trx-card-header rounded-t-lg rounded-b-none">
            <span className="text-[12px] font-semibold text-center">
              Transaction Details
            </span>
          </div>
          <div className="flex flex-col bg-[#FFFFFF] p-[8px] rounded-t-none rounded-b-lg">
            <div className="flex flex-row justify-between mb-1">
              <span className="font-medium text-[12px] text-text-label">
                From address
              </span>
              <CopyButton
                copyText={data?.fromAddress ?? ""}
                copyMessage="Address copied to clipboard!"
                fillColor=""
              />
            </div>
            <span className="text-text-base font-medium text-[12px] break-all mb-2">
              {data?.fromAddress ?? "- -"}
            </span>
            <div className="flex flex-row justify-between">
              <span className="font-medium text-[12px] text-text-label">
                To address
              </span>
              <CopyButton
                copyText={data?.toAddress ?? ""}
                copyMessage="Address copied to clipboard!"
                fillColor=""
              />
            </div>
            <span className="text-text-base font-medium text-[12px] break-all mt-1">
              {data?.toAddress ?? "- -"}
            </span>
            <div className="flex flex-row justify-between w-full mt-[12px]">
              <span className="font-medium text-[12px] text-text-label">
                Transaction Hash
              </span>
              <div className="flex flex-row gap-[8px]">
                {data?.networkSymbol !== "TPNK" && (
                  <RedirectButton
                    transactionHash={data?.transactionHash ?? ""}
                    network={data?.networkSymbol ?? ""}
                    fillColor=""
                  />
                )}
                <CopyButton
                  copyText={data?.transactionHash ?? ""}
                  copyMessage="Transaction Hash copied to clipboard!"
                  fillColor=""
                />
              </div>
            </div>
            <span className="text-text-base font-medium text-[12px] break-all mt-1">
              {data?.transactionHash}
            </span>
            <div className=" p-[8px] mt-[12px] flex flex-row justify-between w-full border-t border-t-border-subtle">
              <div className="flex flex-col items-center">
                <span className="font-medium text-[12px] text-text-label">
                  Amount
                </span>
                <span className="font-medium text-[12px] text-text-base">
                  {constantFunctions.formatAmount(
                    data?.settledAmountRequested ?? 0,
                  )}{" "}
                  {data?.tcs}
                </span>
              </div>
              <div className="w-px bg-border-subtle" />
              <div className="flex flex-col items-center">
                <span className="font-medium text-[12px] text-text-label">
                  Confirmations
                </span>
                <span className="font-medium text-[12px] text-text-base">
                  {data?.confirmationStatus === 1 ||
                  data?.confirmations! > data?.requiredConfirmations! ? (
                    <>
                      {data?.requiredConfirmations} {" / "}
                      {data?.requiredConfirmations}
                    </>
                  ) : (
                    <>
                      {data?.confirmations} {" / "}
                      {data?.requiredConfirmations}
                    </>
                  )}
                </span>
              </div>
              <div className="w-px bg-border-subtle" />
              <div className="flex flex-col">
                <span className="font-medium text-[12px] text-text-label text-right">
                  Status
                </span>
                <DataPill
                  value={
                    data?.confirmationStatus === 0 ? "pending" : "confirmed"
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <MissingTransactionSlider
        isOpen={openMissingTransactionSlider}
        onClose={() => setOpenMissingTransactionSlider(false)}
        txnOrderId={data?.orderId ?? ""}
        network={data?.networkSymbol ?? ""}
      />
    </div>
  );
};

export default CPGOrderFlowDetails;
